import { useCallback, useEffect, useId, useState, type ReactNode } from 'react'

import Tooltip from '@/components/ui/Tooltip'
import { TOUCH_TARGET_Y } from '@/components/ui/button-variants'
import { MARKET_GLOSSARY, type GlossaryTerm } from '@/lib/market-glossary'
import { cn } from '@/lib/utils'

/** The mock's hover wait before a term's card opens (`ls`, 250ms); focus and a tap skip it. */
const HOVER_DELAY_MS = 250

/** A glossary key, or a one-off card for a term the glossary does not hold. */
type TermSource =
  | { term: GlossaryTerm; title?: undefined; body?: undefined }
  | { term?: undefined; title: string; body: string }

type TermTipProps = TermSource & {
  /** The words on screen: "Price (Rs)", "Today across NEPSE". The card's title can differ. */
  children: ReactNode
  /** A closing line on the card, under the explanation: "Not available yet." for a column with no figure. */
  note?: string | undefined
  /**
   * `card` (default): the mock's `.term-tip`, 240 at most, 12px, centred 8 under the words.
   * `band` (sz:kit, owner ruling A3): a table head's card (mock `pi-tip`), 300 wide and 14px,
   * hanging from the head band's foot with its right edge 20px inside the column, fading and
   * dropping 4px over 120ms. For a term in a `DataTable` head (or any `<thead>` / `[data-table-head]`
   * cell); anywhere else it falls back to a centred card below the words.
   */
  variant?: 'card' | 'band' | undefined
  className?: string | undefined
}

/**
 * A label that explains itself (the mock's `.term`): a dotted rule under the words and, on hover
 * (after 250ms), keyboard focus or a tap, a small card centred under them with the term's name and
 * a sentence of what it means. The card is `Tooltip`'s `term` skin; the rule is the `term`
 * utility, quieter inside a table head.
 *
 * Standalone, the label is focusable, and grows to a 44px target below `lg`. Inside a button or a
 * link (a sortable table head) it is not: the control already holds the Tab stop, so its keyboard
 * focus opens the card instead and the control is the node described by it, as the mock wires it.
 */
export default function TermTip({
  term,
  title,
  body,
  children,
  note,
  variant = 'card',
  className,
}: TermTipProps) {
  const entry = term === undefined ? { title, body } : MARKET_GLOSSARY[term]
  const panelId = useId()
  const [control, setControl] = useState<HTMLElement | null>(null)
  const [controlFocused, setControlFocused] = useState(false)

  const bindLabel = useCallback((node: HTMLSpanElement | null) => {
    setControl(node?.parentElement?.closest<HTMLElement>('button, a') ?? null)
  }, [])

  useEffect(() => {
    if (control === null) return
    const onFocus = () => {
      // Keyboard only: a click focuses the button too, and the hover already has the card.
      if (control.matches(':focus-visible')) setControlFocused(true)
    }
    const onBlur = () => {
      setControlFocused(false)
    }
    const previous = control.getAttribute('aria-describedby')
    control.setAttribute('aria-describedby', previous === null ? panelId : `${previous} ${panelId}`)
    control.addEventListener('focus', onFocus)
    control.addEventListener('blur', onBlur)
    return () => {
      control.removeEventListener('focus', onFocus)
      control.removeEventListener('blur', onBlur)
      if (previous === null) control.removeAttribute('aria-describedby')
      else control.setAttribute('aria-describedby', previous)
    }
  }, [control, panelId])

  const inControl = control !== null

  return (
    <Tooltip
      title={entry.title}
      body={entry.body}
      note={note}
      variant={variant === 'band' ? 'band' : 'term'}
      align="center"
      delay={HOVER_DELAY_MS}
      open={controlFocused}
      onDismiss={() => {
        setControlFocused(false)
      }}
      panelId={panelId}
    >
      <span
        ref={bindLabel}
        tabIndex={inControl ? undefined : 0}
        className={cn(
          'term rounded-xs focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-ring',
          inControl ? null : ['relative', TOUCH_TARGET_Y],
          className,
        )}
      >
        {children}
      </span>
    </Tooltip>
  )
}
